import { supabase } from '@/integrations/supabase/client';
import { UserService } from '@/services/users/UserService';
import {
  Portal,
  PortalGenie,
  PortalWithGenies,
  PortalWithCount,
  CreatePortalParams,
  UpdatePortalParams,
  AddGenieToPortalParams,
  UpdatePortalGenieParams,
  DEFAULT_LAYOUT_CONFIG,
} from '@/types/portal/portal';
import { GenieBranding, DEFAULT_BRANDING } from '@/types/ui/branding';

export class PortalService {
  // =====================
  // Portal CRUD Operations
  // =====================

  static async createPortal(params: CreatePortalParams): Promise<Portal | null> {
    try {
      const { data: { user } } = await UserService.getUser();
      if (!user) throw new Error('User not authenticated');

      // Generate slug from name if not provided
      let slug = params.slug || this.generateSlug(params.name);
      slug = await this.ensureUniqueSlug(slug);

      // Get next position
      const { data: portals } = await supabase
        .from('portals')
        .select('position')
        .eq('user_id', user.id)
        .order('position', { ascending: false })
        .limit(1);

      const nextPosition = portals && portals.length > 0 ? (portals[0].position || 0) + 1 : 0;

      const { data, error } = await supabase
        .from('portals')
        .insert({
          user_id: user.id,
          name: params.name,
          slug,
          description: params.description || null,
          branding: params.branding || null,
          layout_type: params.layout_type || 'grid',
          layout_config: params.layout_config || DEFAULT_LAYOUT_CONFIG,
          welcome_message: params.welcome_message || null,
          position: nextPosition,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating portal:', error);
      throw error;
    }
  }

  static async getPortal(portalId: string): Promise<Portal | null> {
    try {
      const { data, error } = await supabase
        .from('portals')
        .select('*')
        .eq('id', portalId)
        .single();

      if (error) return null;
      return data;
    } catch (error) {
      console.error('Error fetching portal:', error);
      return null;
    }
  }

  static async getPortalBySlug(slug: string): Promise<PortalWithGenies | null> {
    try {
      const { data: portal, error } = await supabase
        .from('portals')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error || !portal) return null;

      // Fetch genies with agent data
      const { data: genies } = await supabase
        .from('portal_genies')
        .select(`
          *,
          agent:agents!inner (
            id,
            name,
            description,
            elevenlabs_id,
            branding,
            is_active,
            category
          )
        `)
        .eq('portal_id', portal.id)
        .eq('is_visible', true)
        .order('position', { ascending: true });

      // Fetch url_names for each agent from agent_pages
      // Note: agent_pages links via elevenlabs_agent_id -> agents.elevenlabs_id
      // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
      if (genies && genies.length > 0) {
        const elevenlabsIds = genies
          .map(g => g.agent.elevenlabs_id)
          .filter((id): id is string => !!id);

        if (elevenlabsIds.length > 0) {
          const { data: pages } = await supabase
            .from('agent_pages')
            .select('elevenlabs_agent_id, url_name')
            .in('elevenlabs_agent_id', elevenlabsIds);

          // Attach page data to each genie
          if (pages) {
            const pageMap = new Map(pages.map(p => [p.elevenlabs_agent_id, p]));
            genies.forEach(g => {
              const page = pageMap.get(g.agent.elevenlabs_id);
              if (page) {
                (g.agent as any).page = { url_name: page.url_name };
              }
            });
          }
        }
      }

      return {
        ...portal,
        genies: genies || [],
      };
    } catch (error) {
      console.error('Error fetching portal by slug:', error);
      return null;
    }
  }

  static async listPortals(userId?: string): Promise<PortalWithCount[]> {
    try {
      const { data: { user } } = await UserService.getUser();
      const targetUserId = userId || user?.id;

      if (!targetUserId) return [];

      const { data: portals, error } = await supabase
        .from('portals')
        .select('*')
        .eq('user_id', targetUserId)
        .order('position', { ascending: true });

      if (error) throw error;
      if (!portals || portals.length === 0) return [];

      // Fetch portal genies with agent info for counts + card avatars
      const portalIds = portals.map(p => p.id);
      const { data: portalGenies } = await supabase
        .from('portal_genies')
        .select('portal_id, agent_id, position, agent:agents(name, branding)')
        .in('portal_id', portalIds)
        .order('position', { ascending: true });

      // Build count + summary maps
      const countMap = new Map<string, number>();
      const summaryMap = new Map<string, { agent_id: string; name: string; logoUrl: string | null; primaryColor: string | null }[]>();

      if (portalGenies) {
        portalGenies.forEach((pg: any) => {
          countMap.set(pg.portal_id, (countMap.get(pg.portal_id) || 0) + 1);
          const summaries = summaryMap.get(pg.portal_id) || [];
          if (pg.agent) {
            const agentBranding = pg.agent.branding as GenieBranding | null;
            summaries.push({
              agent_id: pg.agent_id,
              name: pg.agent.name || 'Genie',
              logoUrl: agentBranding?.logoUrl || agentBranding?.customAvatarUrl || null,
              primaryColor: agentBranding?.primaryColor || null,
            });
          }
          summaryMap.set(pg.portal_id, summaries);
        });
      }

      // Merge counts + summaries with portals
      return portals.map(portal => ({
        ...portal,
        genie_count: countMap.get(portal.id) || 0,
        genies_summary: summaryMap.get(portal.id) || [],
      }));
    } catch (error) {
      console.error('Error listing portals:', error);
      return [];
    }
  }

  static async updatePortal(portalId: string, updates: UpdatePortalParams): Promise<Portal | null> {
    try {
      // If slug is being updated, ensure uniqueness
      if (updates.slug) {
        updates.slug = await this.ensureUniqueSlug(updates.slug, portalId);
      }

      const { data, error } = await supabase
        .from('portals')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', portalId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating portal:', error);
      throw error;
    }
  }

  static async deletePortal(portalId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('portals')
        .delete()
        .eq('id', portalId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting portal:', error);
      return false;
    }
  }

  static async reorderPortals(portalIds: string[]): Promise<void> {
    try {
      for (let i = 0; i < portalIds.length; i++) {
        const { error } = await supabase
          .from('portals')
          .update({ position: i })
          .eq('id', portalIds[i]);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error reordering portals:', error);
      throw error;
    }
  }

  // =====================
  // Portal Genie Operations
  // =====================

  static async addGenieToPortal(params: AddGenieToPortalParams): Promise<PortalGenie | null> {
    try {
      // Get next position if not specified
      let position = params.position;
      if (position === undefined) {
        const { data: genies } = await supabase
          .from('portal_genies')
          .select('position')
          .eq('portal_id', params.portal_id)
          .order('position', { ascending: false })
          .limit(1);

        position = genies && genies.length > 0 ? (genies[0].position || 0) + 1 : 0;
      }

      const { data, error } = await supabase
        .from('portal_genies')
        .insert({
          portal_id: params.portal_id,
          agent_id: params.agent_id,
          display_name: params.display_name || null,
          display_description: params.display_description || null,
          custom_avatar_url: params.custom_avatar_url || null,
          position,
          is_featured: params.is_featured || false,
          category_label: params.category_label || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding genie to portal:', error);
      throw error;
    }
  }

  static async updatePortalGenie(
    portalGenieId: number,
    updates: UpdatePortalGenieParams
  ): Promise<PortalGenie | null> {
    try {
      const { data, error } = await supabase
        .from('portal_genies')
        .update(updates)
        .eq('id', portalGenieId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating portal genie:', error);
      throw error;
    }
  }

  static async removeGenieFromPortal(portalId: string, agentId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('portal_genies')
        .delete()
        .eq('portal_id', portalId)
        .eq('agent_id', agentId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing genie from portal:', error);
      return false;
    }
  }

  static async reorderPortalGenies(portalId: string, orderedAgentIds: string[]): Promise<void> {
    try {
      for (let i = 0; i < orderedAgentIds.length; i++) {
        const { error } = await supabase
          .from('portal_genies')
          .update({ position: i })
          .eq('portal_id', portalId)
          .eq('agent_id', orderedAgentIds[i]);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error reordering portal genies:', error);
      throw error;
    }
  }

  static async getPortalGenies(portalId: string): Promise<PortalGenie[]> {
    try {
      const { data, error } = await supabase
        .from('portal_genies')
        .select(`
          *,
          agent:agents (
            id,
            name,
            description,
            elevenlabs_id,
            branding,
            is_active
          )
        `)
        .eq('portal_id', portalId)
        .order('position', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching portal genies:', error);
      return [];
    }
  }

  // =====================
  // Analytics
  // =====================

  static async incrementViewCount(portalId: string): Promise<void> {
    try {
      await supabase.rpc('increment_portal_view_count', { portal_id_param: portalId });
    } catch (error) {
      console.error('Error incrementing portal view count:', error);
    }
  }

  // =====================
  // Password Protection
  // =====================

  static async verifyPortalPassword(portalId: string, password: string): Promise<boolean> {
    // This will be implemented via Edge Function for security
    // For now, return false
    console.warn('Password verification not yet implemented');
    return false;
  }

  static async setPortalPassword(portalId: string, password: string | null): Promise<boolean> {
    // This will be implemented via Edge Function for security
    // The Edge Function will hash the password before storing
    console.warn('Password setting not yet implemented');
    return false;
  }

  // =====================
  // Utility Methods
  // =====================

  private static generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  private static async ensureUniqueSlug(slug: string, excludeId?: string): Promise<string> {
    let finalSlug = slug;
    let counter = 1;

    while (true) {
      let query = supabase
        .from('portals')
        .select('id')
        .eq('slug', finalSlug);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data } = await query.maybeSingle();

      if (!data) break;

      finalSlug = `${slug}-${counter}`;
      counter++;
    }

    return finalSlug;
  }

  // =====================
  // Embed Code Generation
  // =====================

  static generateEmbedCode(portal: Portal, options?: { width?: string; height?: string }): string {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const width = options?.width || '100%';
    const height = options?.height || '600px';

    return `<iframe
  src="${baseUrl}/portal/${portal.slug}/embed"
  width="${width}"
  height="${height}"
  frameborder="0"
  allow="microphone; autoplay"
  style="border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"
></iframe>`;
  }

  static generateShareUrl(portal: Portal): string {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/portal/${portal.slug}`;
  }

  // =====================
  // Branding Resolution
  // =====================

  /**
   * Get account branding for a portal owner
   * Used by public portal pages to resolve branding when portal has no custom branding
   */
  static async getAccountBrandingForPortal(userId: string): Promise<GenieBranding | null> {
    try {
      // Get user's profile to find their team_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('id', userId)
        .single();

      if (profileError || !profile?.team_id) {
        // Fallback: check if user owns a team directly
        const { data: ownedTeam, error: teamError } = await supabase
          .from('team')
          .select('branding')
          .eq('owner_id', userId)
          .maybeSingle();

        if (teamError || !ownedTeam) {
          return null;
        }

        return ownedTeam.branding as GenieBranding | null;
      }

      // Get team branding
      const { data: team, error: teamError } = await supabase
        .from('team')
        .select('branding')
        .eq('id', profile.team_id)
        .single();

      if (teamError || !team) {
        return null;
      }

      return team.branding as GenieBranding | null;
    } catch (error) {
      console.error('Error fetching account branding for portal:', error);
      return null;
    }
  }

  /**
   * Resolve branding for a portal with fallback chain:
   * 1. Portal's custom branding
   * 2. Account/team branding
   * 3. Default branding
   */
  static async resolvePortalBranding(portal: Portal): Promise<GenieBranding> {
    // If portal has custom branding, use it
    if (portal.branding) {
      return portal.branding;
    }

    // Try to get account branding
    const accountBranding = await this.getAccountBrandingForPortal(portal.user_id);
    if (accountBranding) {
      return accountBranding;
    }

    // Fall back to default
    return DEFAULT_BRANDING;
  }
}
